import "../../common/styles/Elements.scss";

import {
  Box,
  Button,
  Grid,
} from "@material-ui/core";
import { ApiErrors } from "common/backend/api-errors/errors";
import { CommonToast, ContentWrapper } from "common/components";
import { CommonErrorDialog } from "common/components/CommonErrorDialog";
import { SubmitButton } from "common/components/input/elements";
import { SaleManager } from "common/components/sale";
import { CreateSale } from "common/graphql/queryDescriptors";
import { useCustomQuery } from "common/store/hooks/useCustomQuery";
import { CreateSaleManageable } from "common/store/sale/types";
import { Mode } from "common/utils/utility";
import { errorMessages } from "common/utils/validation";
import { Form, Formik, FormikErrors } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

export const CreateSalePage = () => {
  // State
  const [error, setError] = useState("");
  const [toastOpen, setToastOpen] = React.useState(false);
  const [uploadInProgress, setUploadInProgress] = React.useState(false);
  const [mode, setMode] = useState(Mode.Edit);

  // Hooks
  const { t } = useTranslation();

  const {
    execute: createSale, loading: createSaleLoading
  } = useCustomQuery(CreateSale({
    onError: (error: ApiErrors) => {
      switch (error) {
        case ApiErrors.SALE_NAME_TAKEN:
          setError("Sale with given name already exists");
          break;
        default:
          setError(error);
          break;
      }
    },
    onSuccess: () => {
      setToastOpen(true);
    },
  }));

  // Option
  const initialValues = new CreateSaleManageable();

  // Validation
  const validate = (values: CreateSaleManageable): FormikErrors<any> => {
    return new CreateSaleManageable(values).validate(
      Object.keys(values),
      errorMessages
    );
  }

  const onSubmit = async (
    values: CreateSaleManageable, { setSubmitting }: any
  ) => {
    setSubmitting(true);

    await createSale({ variables: { input: new CreateSaleManageable(values).serialize() } });

    setSubmitting(false);
  }

  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      onSubmit={onSubmit}
    >
      {({
        values,
        errors,
        isSubmitting,
        touched,
        setFieldValue,
        setValues,
        submitForm,
      }) => {
        const props = {
          values,
          errors,
          isSubmitting,
          touched,
          setFieldValue,
          setValues,
          submitForm,
        };

        const buttonProps = { ...props } as any;
        delete buttonProps.setFieldValue;
        delete buttonProps.setValues;

        const fieldProps = { ...props } as any;
        delete fieldProps.submitForm;
        delete fieldProps.isSubmitting;

        return (
          <Form style={{ width: "100%" }}>
            <SaleManager
              {...fieldProps}
              mode={mode}
              onFilesSelected={() => setUploadInProgress(true)}
              onUploadEnded={() => setUploadInProgress(false)}
              onAutocomplete={() => props.setValues(new CreateSaleManageable(props.values).automatic())}
            />

            <ContentWrapper minHeight="unset" justifyContent="center" padding="50px 11% 50px 11%">
              <Box width="100%" maxWidth="764px">
                <Grid container direction="column" spacing={2}>
                  <Grid item>
                    <Button
                      className="primary"
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={() => setMode(mode === Mode.Edit ? Mode.View : Mode.Edit)}
                    >
                      {mode === Mode.Edit ? t("Preview") : t("Edit")}
                    </Button>
                  </Grid>

                  <Grid item>
                    <SubmitButton
                      value={t("Submit")}
                      {...buttonProps}
                      isSubmitting={createSaleLoading || uploadInProgress}
                    />
                  </Grid>
                </Grid>
              </Box>

              <CommonToast
                open={toastOpen}
                onClose={() => setToastOpen(false)}
                text={t("Sale created!")}
                severity={"success"}
              />

              <CommonErrorDialog
                error={error}
                setError={setError}
              />
            </ContentWrapper>
          </Form>
        );
      }}
    </Formik>
  );
};
